const HTTP_STATUS = Symbol.for('idu.httpStatus');

/**
 * The generated client throws the parsed error *body*, which carries no HTTP status — so by
 * the time an error reaches react-query there is no way to tell a permission refusal from a
 * transient failure. The error interceptor stamps the status here on the way past.
 *
 * A symbol rather than a `status` field: the body is API-shaped data that flows into error
 * displays and logging, and a new enumerable key on it would be indistinguishable from
 * something the API sent.
 */
export const attachHttpStatus = (error: unknown, status: number | undefined): unknown => {
  if (status === undefined || error === null || typeof error !== 'object') return error;

  Object.defineProperty(error, HTTP_STATUS, { value: status, configurable: true });
  return error;
};

export const httpStatusFromError = (error: unknown): number | undefined =>
  error !== null && typeof error === 'object'
    ? (error as Record<symbol, number | undefined>)[HTTP_STATUS]
    : undefined;

/**
 * Authentication and authorization refusals are decisions, not blips: retrying one cannot
 * change the answer, and the second refusal lands ~1s later — by which time the user has
 * often navigated on, so a stale 403 blanks whatever page they are now looking at.
 */
export const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  const status = httpStatusFromError(error);
  if (status === 401 || status === 403) return false;
  return failureCount < 1;
};

const causeCode = (cause: unknown) => (cause as { code?: unknown } | undefined)?.code;

/** spec 026: the refusal code in `error.cause` or a bare `cause`, whichever the client unwrapped. */
export const refusalCodeFromError = (error: unknown): string | undefined => {
  const body = error as { cause?: unknown; error?: { cause?: unknown } } | undefined;
  return [body?.cause, body?.error?.cause]
    .map(causeCode)
    .find((code): code is string => typeof code === 'string');
};
